import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Button } from '@apollo/orbit';
import { compare } from 'compare-versions';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { BuildPipelineTrackDetails } from 'src/lib/graphqlTypes/types';

import { FED_NEXT_VERSION } from '../../../lib/config/config';
import { GraphRef } from '../hooks/useGraphRef';
import { variantSettingsRouteConfig } from '../routes';

// Check if new minor version of Federation is available and return appropriate content
const newMinorVersionAvailable = (
  allowedTracks: Pick<
    BuildPipelineTrackDetails,
    'compositionVersion' | 'minimumRouterVersion'
  >[],
  compositionVersion: string,
) => {
  // If a variant is on `FED_NEXT` the GraphVariant.compositionVersion
  // won't be semver compatible. Exit early otherwise compare() will error below.
  if (compositionVersion === FED_NEXT_VERSION) {
    return false;
  }

  return allowedTracks.some((track) => {
    const versionMinusPatch = track.compositionVersion.slice(
      0,
      track.compositionVersion.lastIndexOf('.'),
    );
    return compare(versionMinusPatch, compositionVersion, '>');
  });
};

// Return content based on whether a new Federation version is available.
// If there's a new minor version available, it prompts the user to update.
export const FederationVersion = ({
  availableTracks,
  federationVersion,
  graphRef,
}: {
  availableTracks: Array<
    { __typename: 'BuildPipelineTrackDetails' } & Pick<
      BuildPipelineTrackDetails,
      'compositionVersion' | 'minimumRouterVersion'
    >
  >;
  federationVersion: string;
  graphRef: GraphRef;
}) => {
  const history = useHistory();

  return newMinorVersionAvailable(availableTracks, federationVersion) ? (
    <div className="flex flex-row items-center">
      <p>Update Available</p>
      <Tooltip
        interactive
        label={
          <div className="font-normal">
            There is a new Federation version available.
            <Button
              variant="buttonLink"
              className="mr-1"
              onClick={() => {
                history.push(
                  variantSettingsRouteConfig.path({
                    graphId: graphRef?.graphId as string,
                    graphVariant: graphRef?.graphVariant as string,
                    tab: 'general',
                  }),
                );
              }}
            >
              Update to the latest version
            </Button>
            to ensure you have the latest features and bug fixes.
          </div>
        }
      >
        <IconInfo className="ml-2 text-secondary" />
      </Tooltip>
    </div>
  ) : (
    <p>You are up-to-date</p>
  );
};
